import { FormControl } from '@angular/forms';
import { SlotCompactInfo } from '../../models/allocation-view-calendar/allocation-calendar-slot.model';
import { AddressModel } from '../../../../../../common/models/address.model';
import { CarrierModel } from '../../../../../../common/models/carrier.model';
import { ControlProperty } from '../../../../../../common/models/forms.model';
import { EmissionStage } from '../../../../../../common/constants/emission-stage';
import { FCMGoodsType, UliActivityType } from '../../../../../../common/constants/uli';
import { TruckSize } from '../../../../../../common/constants/truck-size';
import { LocalDate, LocalDateModel } from '../../../../../models';
import {
  CompartmentRuleItem,
  CompartmentSapModel,
  DriverIdType,
  GeofenceConfigModel,
  TruckLoadingType,
} from '../../../../administration';
import { MultiStopUliInformation } from './uli-multi-stop-creation.model';
import { AllocationCalendarDockModel } from '../../models';

// todo: split file

export interface ActiveCompartmentsModel {
  id: number;
  name: string;
  ssccId: string;
  compartmentAddresses: AddressModel[];
  yardTime1: number;
  yardTime2: number;
  onTime: number;
  crossDockEnabled: boolean;
  active: boolean;
  in: boolean;
  out: boolean;
  autoAllocationEnabled: boolean;
  compartmentSapClientViews: CompartmentSapModel[];
  geofenceConfig: GeofenceConfigModel;
}

export class UliInformation {
  loadNumber: string = null;
  pickupPartyAddress: AddressModel;
  deliveryPartyAddress: AddressModel;
  dangerousGoods = false;
  customsGoods = false;
  scheduledRegistrationTime: Date = null;
  numberOfHandlingUnits: number = null;
  documentNumbers: string[] = [];
  type = UliActivityType.Outbound;
  weightGross: number = null;
  loadingMeters: number = null;
  generateLoadNumber = false;
  goodsType: FCMGoodsType = FCMGoodsType.Others;

  constructor(params?: UliInformation) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliInformationForm {
  constructor(model: UliInformation) {
    return {
      loadNumber: new FormControl(model.loadNumber),
      pickupPartyAddress: new FormControl(model.pickupPartyAddress),
      deliveryPartyAddress: new FormControl(model.deliveryPartyAddress),
      dangerousGoods: new FormControl(model.dangerousGoods),
      customsGoods: new FormControl(model.customsGoods),
      scheduledRegistrationTime: new FormControl(model.scheduledRegistrationTime),
      numberOfHandlingUnits: new FormControl(model.numberOfHandlingUnits),
      documentNumbers: new FormControl(model.documentNumbers),
      type: new FormControl(model.type),
      weightGross: new FormControl(model.weightGross),
      loadingMeters: new FormControl(model.loadingMeters),
      generateLoadNumber: new FormControl(model.generateLoadNumber),
      goodsType: new FormControl(model.goodsType),
    };
  }
}

// this extended class should be phased out after form creation fully refactored
export class UliInformationExtended extends UliInformation {
  uli?: string;

  constructor(params: UliInformationExtended | MultiStopUliInformation) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliCreationResponse extends UliInformation {
  uli: string = null;

  constructor(params?: UliCreationResponse) {
    super(params);
    if (params) {
      Object.assign(this, params);
    }
  }
}

export interface UliInformationOutputModel extends Omit<UliInformation, 'scheduledRegistrationTime'> {
  scheduledRegistrationTime: LocalDateModel;
}

export interface TwoStopUliFormModel {
  uliInformation: UliInformation;
  truckInformation: UliTruckInformation;
  contactData: UliContactInformation;
  driverInformation: UliDriverInformation;
}

export interface ChinaDriverPermitPayload {
  truckPlate: string;
  permitType: DriverIdType;
  permitNumber: string;
}

// Exporting multi-stop related models to save legacy code from breaking
export {
  MultiStopUliInformation,
  MultiStopUliFormModel,
  TransportOrderInformation,
  MultiStopUliInformationOutputModel,
  TransportOrderInformationOutputModel,
} from './uli-multi-stop-creation.model';

export class UliTruckInformation {
  customsGoods = false;
  dangerousGoods = false;
  loadingTypes: TruckLoadingType[] = [];
  truckPlateId: string = null;
  truckPlateIdCountryOfOrigin: string = null;
  trailerPlateId: string = null;
  trailerPlateIdCountryOfOrigin: string = null;
  truckSize = null;
  emissionStage: EmissionStage;

  constructor(params?: UliTruckInformation) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliTruckInformationForm {
  constructor(
    model: UliTruckInformation,
    public loadingTypes: FormControl<TruckLoadingType[]> = new FormControl(model.loadingTypes),
    public truckPlateId: ControlProperty<string> = model.truckPlateId,
    public trailerPlateId: ControlProperty<string> = model.trailerPlateId,
    public truckPlateIdCountryOfOrigin: ControlProperty<string> = model.truckPlateIdCountryOfOrigin,
    public trailerPlateIdCountryOfOrigin: ControlProperty<string> = model.trailerPlateIdCountryOfOrigin,
    public customsGoods: ControlProperty<boolean> = model.customsGoods,
    public dangerousGoods: ControlProperty<boolean> = model.dangerousGoods,
    public truckSize: ControlProperty<TruckSize> = model.truckSize,
    public emissionStage: ControlProperty<EmissionStage> = model.emissionStage,
  ) {}
}

export class UliContactInformation {
  carrierEmail: string = null;
  carrierName: string = null;
  carrierExtId: string = null;
  carrierPhone: string = null;
  rbTransportPlannerEmail: string = null;
  rbTransportPlannerPhone: string = null;

  constructor(params?: UliContactInformation) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliContactInformationForm {
  constructor(
    model: UliContactInformation,
    public carrierName: ControlProperty<string> = model.carrierName,
    public carrierExtId: ControlProperty<string> = model.carrierExtId,
    public carrierEmail: ControlProperty<string> = model.carrierEmail,
    public carrierPhone: ControlProperty<string> = model.carrierPhone,
    public rbTransportPlannerEmail: ControlProperty<string> = model.rbTransportPlannerEmail,
    public rbTransportPlannerPhone: ControlProperty<string> = model.rbTransportPlannerPhone,
  ) {}
}

export class UliDriverInformation {
  firstName: string = null;
  lastName: string = null;
  loanerPhoneId: string = null;
  permitNumber: string = null;
  permitType: DriverIdType = null;
  phoneNumber: string = null;
  frequentDriverId: number = null;

  constructor(params?: UliDriverInformation) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliDriverInformationForm {
  constructor(
    model: UliDriverInformation,
    public firstName: ControlProperty<string> = model.lastName,
    public lastName: ControlProperty<string> = model.lastName,
    public loanerPhoneId: ControlProperty<string> = model.loanerPhoneId,
    public permitNumber: ControlProperty<string> = [model.permitNumber, []],
    public permitType: ControlProperty<DriverIdType> = model.permitType,
    public phoneNumber: ControlProperty<string> = model.phoneNumber,
    public frequentDriverId: ControlProperty<number> = model.frequentDriverId, //TODO check if it's still needed
  ) {}
}

export class UliFormModel {
  uliInformation: UliInformation | MultiStopUliInformation;
  truckInformation: UliTruckInformation;
  contactData: UliContactInformation;
  driverInformation: UliDriverInformation;

  constructor(params?: UliFormModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export interface UliRuleParams {
  rule: CompartmentRuleItem;
  slot: SlotCompactInfo;
  hubTimezone: string;
  dockInformation: AllocationCalendarDockModel;
}

export class UliRule {
  uliInformation: UliInformation = null;
  truckInformation: UliTruckInformation = null;
  contactData: UliContactInformation = null;
  pickupAddressOptions: AddressModel[];
  deliveryAddressOptions: AddressModel[];
  truckPlateOptions: string[];
  carrierOptions: CarrierModel[];
  slot: SlotCompactInfo = null;
  hubTimezone: string;
  dockInformation: AllocationCalendarDockModel;

  constructor(params: UliRuleParams | UliRule) {
    if (ruleUliTypeGuard(params)) {
      Object.assign(this, params);

      this.uliInformation.scheduledRegistrationTime = this.createScheduledRegistrationTime(
        params?.slot?.timeFrame?.start,
      );
    } else if (params) {
      this.slot = params.slot;
      this.fillUliInformationForm(params);
      this.fillTruckInformationForm(params.rule);
      this.fillContactDataForm(params.rule);
      this.hubTimezone = params.hubTimezone;
      this.dockInformation = params.dockInformation;
    }
  }

  private fillUliInformationForm({ rule, slot }: UliRuleParams): void {
    const uliInformationParams = new UliInformation();
    const activityTypes = rule.activityTypes;
    const pickupAddresses = rule.pickupAddresses;
    const deliveryAddresses = rule.deliveryAddresses;
    const start = slot?.timeFrame?.start;
    const startMoment = this.createScheduledRegistrationTime(start);

    if (!activityTypes.length && !pickupAddresses.length && !deliveryAddresses.length && !startMoment) {
      return;
    }

    if (activityTypes.length === 1) {
      uliInformationParams.type = activityTypes[0];
    } else if (activityTypes.length > 1) {
      uliInformationParams.type = null;
    }

    if (pickupAddresses.length === 1) {
      uliInformationParams.pickupPartyAddress = pickupAddresses[0];
    } else if (pickupAddresses.length > 1) {
      this.pickupAddressOptions = pickupAddresses;
    }

    if (deliveryAddresses.length === 1) {
      uliInformationParams.deliveryPartyAddress = deliveryAddresses[0];
    } else if (deliveryAddresses.length > 1) {
      this.deliveryAddressOptions = deliveryAddresses;
    }

    if (startMoment) {
      uliInformationParams.scheduledRegistrationTime = startMoment;
    }

    this.uliInformation = uliInformationParams;
  }

  private fillTruckInformationForm(rule: CompartmentRuleItem): void {
    const { truckPlates } = rule;
    const truckPlatesLength = truckPlates.length;
    const truckInformationParams = new UliTruckInformation();

    this.truckInformation = truckInformationParams;

    if (!truckPlatesLength) {
      return;
    }

    if (truckPlatesLength === 1) {
      truckInformationParams.truckPlateId = truckPlates[0];

      this.truckInformation = truckInformationParams;
    } else {
      this.truckPlateOptions = truckPlates;
    }
  }

  private fillContactDataForm(params: CompartmentRuleItem): void {
    const { carrier, subCarriers } = params;

    if (!carrier) {
      return;
    }

    if (!subCarriers.length) {
      const contactDataParams = new UliContactInformation();

      contactDataParams.carrierName = carrier.name;
      contactDataParams.carrierExtId = carrier.extId;
      this.contactData = contactDataParams;
    } else {
      this.carrierOptions = subCarriers.concat(carrier);
    }
  }

  private createScheduledRegistrationTime(start: LocalDate): Date {
    return new Date(start.timestamp);
  }
}

export class RuleAllocationDetails {
  compartmentId: number;
  ruleId: number;
  hubId: number;
  slot: SlotCompactInfo;

  constructor(params?: RuleAllocationDetails) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliForm {
  constructor(
    model: UliFormModel,
    public uliInformation: ControlProperty<UliInformation | MultiStopUliInformation> = model.uliInformation,
    public truckInformation: ControlProperty<UliTruckInformation> = model.truckInformation,
    public contactData: ControlProperty<UliContactInformation> = model.contactData,
    public driverInformation: ControlProperty<UliDriverInformation> = model.driverInformation,
  ) {}
}

export const ruleUliTypeGuard = (data: UliRule | UliRuleParams): data is UliRule => {
  return !!(data as UliRule)?.uliInformation;
};
